import React, { useCallback, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    FullMigrationOptions,
    GlOnlyNote,
    MigrationDateRange,
    getHistoricalDataSource,
    getSubView,
    useHistoricalDataToggles,
    useQbdJsonHistoricalDataDefaults,
    isMigrationDateRangeValid,
} from "@components/accountingMigration/historicalData";
import { MigrationFormValues } from "./accountingMigrationForm";
import styles from "./AccountingMigrationModal.module.scss";

interface MigrationHistoricalDataStepProps {
    entityName: string | null | undefined;
    isQboConnected: boolean;
    quickbooksCompanyName?: string | null;
    onBack: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
}

export const MigrationHistoricalDataStep: React.FC<
    MigrationHistoricalDataStepProps
> = ({
    entityName,
    isQboConnected,
    quickbooksCompanyName,
    onBack,
    onSubmit,
    isSubmitting,
}) => {
    const { values, setFieldValue } = useFormikContext<MigrationFormValues>();
    const { historicalData, migrationMethod } = values;

    const isNonQboMigration =
        !!migrationMethod &&
        migrationMethod !== AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;

    const isQbdJsonMigration =
        migrationMethod ===
        AccountingMigrationExternalSystem.QUICKBOOKS_DESKTOP;

    const subView = useMemo(
        () =>
            getSubView(
                getHistoricalDataSource({
                    isQboConnected,
                    isQbdJsonMigration,
                    isNonQboMigration,
                    selectedSystem: null,
                }),
            ),
        [isQboConnected, isNonQboMigration, isQbdJsonMigration],
    );

    useQbdJsonHistoricalDataDefaults({
        isQbdJsonMigration,
        historicalData,
        setFieldValue,
    });

    useEffect(() => {
        if (subView === "glOnly") {
            if (!historicalData.syncGeneralLedger) {
                setFieldValue("historicalData.syncGeneralLedger", true);
            }
            if (historicalData.syncCounterparties) {
                setFieldValue("historicalData.syncCounterparties", false);
            }
            if (historicalData.syncAttachments) {
                setFieldValue("historicalData.syncAttachments", false);
            }
            if (historicalData.syncClasses) {
                setFieldValue("historicalData.syncClasses", false);
            }
            if (historicalData.syncCatalogItems) {
                setFieldValue("historicalData.syncCatalogItems", false);
            }
            if (historicalData.syncInvoices) {
                setFieldValue("historicalData.syncInvoices", false);
            }
            if (historicalData.syncBills) {
                setFieldValue("historicalData.syncBills", false);
            }
        }
    }, [
        subView,
        historicalData.syncGeneralLedger,
        historicalData.syncCounterparties,
        historicalData.syncAttachments,
        historicalData.syncClasses,
        historicalData.syncCatalogItems,
        historicalData.syncInvoices,
        historicalData.syncBills,
        setFieldValue,
    ]);

    const toggles = useHistoricalDataToggles({
        historicalData,
        setFieldValue,
    });

    const handleStartDateChange = useCallback(
        (value: string | undefined) => {
            setFieldValue("historicalData.startDate", value);
        },
        [setFieldValue],
    );

    const canSubmit =
        isMigrationDateRangeValid({
            startDate: historicalData.startDate,
            endDate: historicalData.endDate,
        }) &&
        (historicalData.syncGeneralLedger ||
            historicalData.syncCounterparties ||
            historicalData.syncAttachments ||
            historicalData.syncClasses ||
            historicalData.syncCatalogItems ||
            historicalData.syncInvoices ||
            historicalData.syncBills);

    return (
        <>
            <StandardModalHeader>
                <VStack gap={4}>
                    <span className={styles.stepLabel}>Step 3 of 3</span>
                    <div>
                        Migration options
                        {entityName && ` for ${entityName}`}
                    </div>
                </VStack>
            </StandardModalHeader>
            <VStack gap={24}>
                <VStack gap={16}>
                    <VStack gap={8}>
                        {quickbooksCompanyName && (
                            <div className={styles.description}>
                                QuickBooks company: {quickbooksCompanyName}
                            </div>
                        )}
                        <div className={styles.description}>
                            Choose what to migrate and the date range for
                            journal entries
                        </div>
                    </VStack>
                    <div className={styles.migrationOptionsBox}>
                        {subView === "full" && (
                            <FullMigrationOptions
                                syncGeneralLedger={
                                    historicalData.syncGeneralLedger
                                }
                                syncCounterparties={
                                    historicalData.syncCounterparties
                                }
                                syncAttachments={historicalData.syncAttachments}
                                syncClasses={historicalData.syncClasses}
                                syncCatalogItems={
                                    historicalData.syncCatalogItems
                                }
                                syncInvoices={historicalData.syncInvoices}
                                syncBills={historicalData.syncBills}
                                onToggleGeneralLedger={
                                    toggles.onToggleGeneralLedger
                                }
                                onToggleCounterparties={
                                    toggles.onToggleCounterparties
                                }
                                onToggleAttachments={
                                    toggles.onToggleAttachments
                                }
                                onToggleClasses={toggles.onToggleClasses}
                                onToggleCatalogItems={
                                    toggles.onToggleCatalogItems
                                }
                                onToggleInvoices={toggles.onToggleInvoices}
                                onToggleBills={toggles.onToggleBills}
                                hideAttachments={isQbdJsonMigration}
                                hideCounterparties={isQbdJsonMigration}
                            />
                        )}
                        {subView === "glOnly" && <GlOnlyNote />}
                        <MigrationDateRange
                            startDate={historicalData.startDate}
                            endDate={historicalData.endDate}
                            onStartDateChange={handleStartDateChange}
                        />
                    </div>
                </VStack>
                <HStack gap={8} justify="end">
                    <Button size="md" variant="secondary" onClick={onBack}>
                        Back
                    </Button>
                    <ButtonWithLoader
                        size="md"
                        variant="primary"
                        onClick={onSubmit}
                        loading={isSubmitting}
                        disabled={!canSubmit}
                    >
                        Continue
                    </ButtonWithLoader>
                </HStack>
            </VStack>
        </>
    );
};
